"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useState, useRef } from "react";
import { CircleCheck, Loader, Sparkles, House, Calendar, User } from "lucide-react";
import { useForm, FormProvider } from "react-hook-form";
import { FormField, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
function FormItem({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={className ? `space-y-2 ${className}` : "space-y-2"} {...props} />;
}
const inputBaseClasses = "flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors";
const textareaBaseClasses = "flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors resize-y font-default";
interface QuoteFormValues {
  full_name: string;
  email: string;
  phone_number: string;
  home_address: string;
  bedrooms: string;
  bathrooms: string;
  square_footage: string;
  preferred_date: string;
  in_person_quote: boolean;
  message: string;
}
const defaultValues: QuoteFormValues = {
  full_name: "",
  email: "",
  phone_number: "",
  home_address: "",
  bedrooms: "",
  bathrooms: "",
  square_footage: "",
  preferred_date: "",
  in_person_quote: false,
  message: ""
};
const DEFAULT_SUCCESS_MESSAGE = "Tack! Vi har tagit emot dina uppgifter och återkommer till dig personligen inom 24 timmar för att diskutera din renovering.";
function QuoteForm() {
  const [formDef, setFormDef] = useState<any>(null);
  const [hpValue, setHpValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState(DEFAULT_SUCCESS_MESSAGE);
  const fetchTimeRef = useRef<number>(Date.now());
  const submittingRef = useRef<boolean>(false);
  const formMethods = useForm<QuoteFormValues>({
    defaultValues,
    mode: "onBlur"
  });
  const {
    control,
    handleSubmit,
    reset
  } = formMethods;
  useEffect(() => {
    const client = getWvcClient();
    if (!client?.pluginRequest) return;
    fetchTimeRef.current = Date.now();
    client.pluginRequest("wvc-forms/v1/forms/bathroom_quote_form_quote_request").then((data: any) => {
      setFormDef(data);
    }).catch((err: any) => {
      console.warn("Could not load form definition:", err);
    });
  }, []);
  const onSubmit = async (values: QuoteFormValues) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const client = getWvcClient();
      if (!client?.pluginRequest) {
        throw new Error("Formulärklienten är inte tillgänglig för tillfället.");
      }

      // Respect spam.min_seconds
      const minSeconds = formDef?.spam?.min_seconds || 3;
      const elapsedSeconds = (Date.now() - fetchTimeRef.current) / 1000;
      if (elapsedSeconds < minSeconds) {
        await new Promise(resolve => setTimeout(resolve, Math.ceil((minSeconds - elapsedSeconds) * 1000)));
      }

      // Map values by field id if definition is present, otherwise send by key
      const payloadValues: Record<string, any> = {};
      const entries = Object.entries(values) as [keyof QuoteFormValues, any][];
      entries.forEach(([key, value]) => {
        const fieldMatch = formDef?.fields?.find((f: any) => f.key === key);
        const resolvedId = fieldMatch ? fieldMatch.id : key;
        if (key === "in_person_quote") {
          payloadValues[resolvedId] = value ? "1" : "";
        } else if (value !== undefined && value !== null) {
          payloadValues[resolvedId] = String(value);
        }
      });
      const token = formDef?.spam?.token || "";
      const hpField = formDef?.spam?.honeypot?.field;
      const hpData = hpField ? {
        [hpField]: hpValue
      } : undefined;
      const res = await client.pluginRequest("wvc-forms/v1/forms/bathroom_quote_form_quote_request/submit", {
        method: "POST",
        data: {
          values: payloadValues,
          token,
          hp: hpData,
          meta: {
            source_url: typeof window !== "undefined" ? window.location.href : "",
            referrer: typeof document !== "undefined" ? document.referrer : ""
          }
        }
      });
      if (res?.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation?.url) {
          window.location.assign(res.confirmation.url);
          return;
        }
        if (res.confirmation?.message) {
          const stripped = res.confirmation.message.replace(/<[^>]+>/g, "").trim();
          setSuccessMessage(stripped || DEFAULT_SUCCESS_MESSAGE);
        } else {
          setSuccessMessage(DEFAULT_SUCCESS_MESSAGE);
        }
        setIsSubmitted(true);
      } else if (res?.code === "frms_validation_failed" && res?.errors) {
        const firstErrorKey = Object.keys(res.errors)[0];
        const errorMsg = res.errors[firstErrorKey]?.message || res.message || "Vänligen kontrollera dina inmatade uppgifter.";
        setSubmitError(errorMsg);
      } else {
        setSubmitError(res?.message || "Ett fel uppstod när din förfrågan skulle skickas. Vänligen försök igen.");
      }
    } catch (err: any) {
      setSubmitError(err?.message || "Kunde inte skicka förfrågan. Vänligen kontrollera din internetanslutning och försök igen.");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };
  const handleReset = () => {
    reset(defaultValues);
    setHpValue("");
    setIsSubmitted(false);
    setSubmitError(null);
    setSuccessMessage(DEFAULT_SUCCESS_MESSAGE);
    fetchTimeRef.current = Date.now();
  };
  if (isSubmitted && !submitError) {
    return <div data-section-id="125803" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-full bg-primary/10 flex items-center justify-center">
          <CircleCheck className="size-10 text-primary" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-semibold tracking-tight">Tack för din förfrågan!</h3>
          <p className="text-muted-foreground max-w-md mx-auto font-default">
            {successMessage}
          </p>
        </div>
        <Button onClick={handleReset} variant="outline" className="mt-4 font-default">
          Skicka en ny förfrågan
        </Button>
      </div>;
  }
  return <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {formDef?.spam?.honeypot && <div style={{
        position: "absolute",
        left: "-9999px"
      }} aria-hidden="true">
            <input type="text" name={formDef.spam.honeypot.field} tabIndex={-1} autoComplete="off" value={hpValue} onChange={e => setHpValue(e.target.value)} />
          </div>}

        {/* 1. Kontaktuppgifter */}
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-primary">
            <User className="size-5" />
            <h3 className="text-lg font-default font-medium">1. Kontaktuppgifter</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="full_name" rules={{
            required: "Namn är obligatoriskt"
          }} render={({
            field
          }) => <FormItem className="col-span-1 md:col-span-2">
                  <FormLabel className="font-default">Ditt fullständiga namn</FormLabel>
                  <FormControl>
                    <input {...field} type="text" className={inputBaseClasses} placeholder="Anna Andersson" />
                  </FormControl>
                  <FormDescription className="font-default text-xs">Ange för- och efternamn.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "E-postadress är obligatorisk",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Vänligen ange en giltig e-postadress"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default">E-postadress</FormLabel>
                  <FormControl>
                    <input {...field} type="email" className={inputBaseClasses} placeholder="anna.andersson@exempel.se" />
                  </FormControl>
                  <FormDescription className="font-default text-xs">Vi skickar offerten till denna adress.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone_number" rules={{
            required: "Telefonnummer är obligatoriskt",
            pattern: {
              value: /^\+?[0-9\s\-\(\)]+$/,
              message: "Vänligen ange ett giltigt telefonnummer"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default">Telefonnummer</FormLabel>
                  <FormControl>
                    <input {...field} type="tel" className={inputBaseClasses} placeholder="070-123 45 67" />
                  </FormControl>
                  <FormDescription className="font-default text-xs">Det nummer vi bäst når dig på.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="home_address" rules={{
            required: "Adress är obligatoriskt"
          }} render={({
            field
          }) => <FormItem className="col-span-1 md:col-span-2">
                  <FormLabel className="font-default">Adress för renovering</FormLabel>
                  <FormControl>
                    <input {...field} type="text" className={inputBaseClasses} placeholder="Storgatan 12, 652 25 Karlstad" />
                  </FormControl>
                  <FormDescription className="font-default text-xs">Gatuadress där badrummet är beläget.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        <Separator className="bg-border/50" />

        {/* 2. Detaljer om projektet */}
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-primary">
            <House className="size-5" />
            <h3 className="text-lg font-default font-medium">2. Detaljer om projektet</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormField control={control} name="bedrooms" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default">Antal badrum</FormLabel>
                  <FormControl>
                    <input {...field} type="number" min="0" className={inputBaseClasses} placeholder="1" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="bathrooms" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default">Gäst-WC</FormLabel>
                  <FormControl>
                    <input {...field} type="number" min="0" className={inputBaseClasses} placeholder="0" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="square_footage" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default">Yta (kvm)</FormLabel>
                  <FormControl>
                    <input {...field} type="number" min="0" className={inputBaseClasses} placeholder="8" />
                  </FormControl>
                  <FormDescription className="font-default text-xs">Ungefärlig totalyta.</FormDescription>
                  <FormMessage />
                </FormItem>} />
          </div>
        </div>

        <Separator className="bg-border/50" />

        {/* 3. Planering & Besök */}
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-primary">
            <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
            <h3 className="text-lg font-default font-medium">3. Planering & Besök</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormField control={control} name="preferred_date" render={({
            field
          }) => <FormItem>
                  <FormLabel className="font-default">Önskad byggstart</FormLabel>
                  <FormControl>
                    <input {...field} type="date" className={inputBaseClasses} />
                  </FormControl>
                  <FormDescription className="font-default text-xs">Välj när du önskar påbörja renoveringen.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="in_person_quote" render={({
            field
          }) => <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border/50 p-4 shadow-sm bg-secondary/50 h-full">
                  <div className="space-y-0.5 pr-4">
                    <FormLabel className="text-base font-default">
                      Boka ett kostnadsfritt hembesök
                    </FormLabel>
                    <FormDescription className="font-default text-xs">
                      Vi kommer gärna ut och tittar på förutsättningarna innan offert.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>} />
          </div>
        </div>

        {/* New message field */}
        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default">Övrigt eller beskrivning av projektet</FormLabel>
              <FormControl>
                <textarea {...field} rows={5} className={textareaBaseClasses} placeholder="Beskriv ditt projekt, speciella önskemål eller ställ dina frågor här..." />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-default">
            {submitError}
          </div>}

        <div className="pt-4">
          <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-primary hover:bg-primary/90 text-primary-foreground font-default rounded-md transition-all">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Skickar förfrågan...
              </> : <>
                <Sparkles className="mr-2 size-5" />
                Skicka min förfrågan
              </>}
          </Button>
          <p className="text-xs text-muted-foreground mt-4 text-center md:text-left font-default">
            Genom att skicka formuläret godkänner du att PROFFSBADRUM.SE kontaktar dig gällande din förfrågan personligen inom 24 timmar.
          </p>
        </div>
      </form>
    </FormProvider>;
}
export default function RequestYourFreeQuote() {
  return <section id="ditt-nya-badrum-börjar-här" className="bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/30 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="font-default font-bold tracking-tight text-foreground mb-4 !text-5xl">
            Begär offert för badrumsrenovering med fast pris
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Vi återkommer personligen för att diskutera din badrumsrenovering och hjälper dig vidare mot ett kostnadsförslag anpassat efter dina önskemål och projektets förutsättningar.
          </p>
        </div>

        <Card className="max-w-4xl mx-auto shadow-lg border-border/50 bg-card/95 backdrop-blur-sm rounded-lg overflow-hidden">
          <CardHeader className="bg-secondary/30 border-b border-border/50 pb-8 pt-8">
            <h3 className="text-2xl font-default font-semibold">Underlag för badrumsoffert</h3>
            <CardDescription className="text-base font-default">
              Fyll i uppgifterna nedan om din badrumsrenovering så får vi ett bättre underlag för att bedöma projektets omfattning och ta fram ett relevant kostnadsförslag.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-8 pb-10 px-6 md:px-10">
            <QuoteForm />
          </CardContent>
        </Card>
      </div>
    </section>;
}